import { API_CONFIG } from "./ApiConfig";

class BookingService {
  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.headers = this.getAuthHeaders();
  }

  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    return headers;
  }

  async handleResponse(response) {
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: 'An error occurred'
      }));
      throw new Error(error.message || `HTTP Error: ${response.status}`);
    }
    return response.json();
  }

  // Get appointments (with optional filters)
  async getAppointments(filters = {}) {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const url = `${this.baseURL}/appointments${queryParams ? '?' + queryParams : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.headers,
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    }
  }

  // Search appointments by hash
  async searchAppointmentsByHash(hash) {
    try {
      const response = await fetch(
        `${this.baseURL}/appointments?q=${encodeURIComponent(hash)}&sort=id`,
        {
          method: 'GET',
          headers: this.headers,
        }
      );
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error searching appointments:', error);
      throw error;
    }
  }

  // Get appointment by ID
  async getAppointment(appointmentId) {
    try {
      const response = await fetch(`${this.baseURL}/appointments/${appointmentId}`, {
        method: 'GET',
        headers: this.headers,
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching appointment:', error);
      throw error;
    }
  }

  // Update appointment
  async updateAppointment(appointmentId, appointmentData) {
    try {
      const response = await fetch(`${this.baseURL}/appointments/${appointmentId}`, {
        method: 'PUT',
        headers: this.headers,
        body: JSON.stringify(appointmentData),
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error updating appointment:', error);
      throw error;
    }
  }

  // Delete appointment
  async deleteAppointment(appointmentId) {
    try {
      const response = await fetch(`${this.baseURL}/appointments/${appointmentId}`, {
        method: 'DELETE',
        headers: this.headers,
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error deleting appointment:', error);
      throw error;
    }
  }

  // Get customer by ID
  async getCustomer(customerId) {
    try {
      const response = await fetch(`${this.baseURL}/customers/${customerId}`, {
        method: 'GET',
        headers: this.headers,
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching customer:', error);
      throw error;
    }
  }

  // Get all appointments for a specific customer
  async getCustomerAppointments(customerId) {
    try {
      // Search for appointments by customer ID
      const allAppointments = await this.getAppointments();
      return allAppointments.filter(apt => apt.customerId === customerId);
    } catch (error) {
      console.error('Error fetching customer appointments:', error);
      throw error;
    }
  }

  // Update customer
  async updateCustomer(customerId, customerData) {
    try {
      const response = await fetch(`${this.baseURL}/customers/${customerId}`, {
        method: 'PUT',
        headers: this.headers,
        body: JSON.stringify(customerData),
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  }



  // Get all services
  async getServices() {
    try {
      const response = await fetch(`${this.baseURL}/services`, {
        method: 'GET',
        headers: this.headers,
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching services:', error);
      throw error;
    }
  }


  // Get all providers
  async getProviders() {
    try {
      const response = await fetch(`${this.baseURL}/providers`, {
        method: 'GET',
        headers: this.headers,
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching providers:', error);
      throw error;
    }
  }

  // Get available time slots for a service, provider, and date
  async getAvailabilities(providerId, serviceId, date) {
    try {
      const formattedDate = this.formatDate(date);
      const response = await fetch(
        `${this.baseURL}/availabilities?providerId=${providerId}&serviceId=${serviceId}&date=${formattedDate}`,
        {
          method: 'GET',
          headers: this.headers,
        }
      );
      return this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching availabilities:', error);
      throw error;
    }
  }

  // Book appointment (complete flow: create/find customer + create appointment)
  async bookAppointment(bookingData) {
    try {

      const response = await fetch(`${this.baseURL}/book`, { method: "POST", headers: this.getAuthHeaders(), body: JSON.stringify({...bookingData, lastName: bookingData.lastName ? bookingData.lastName : "--" })});

      return this.handleResponse(response)
    } catch (error) {
      console.error('Error booking appointment:', error);
      throw error;
    }
  }

  // Helper: Format date to Y-m-d
  formatDate(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Helper: Format datetime to Y-m-d H:i:s
  formatDateTime(date, time) {
    const formattedDate = this.formatDate(date);
    const [timePart, meridiem] = time.split(' ');
    let [hours, minutes] = timePart.split(':').map(Number);

    if (meridiem === 'PM' && hours !== 12) {
      hours += 12;
    } else if (meridiem === 'AM' && hours === 12) {
      hours = 0;
    }

    const formattedTime = `${String(hours).padStart(2, '0')}:${String(
      minutes
    ).padStart(2, '0')}:00`;
    return `${formattedDate} ${formattedTime}`;
  }

  // Helper: Calculate end time based on duration
  calculateEndTime(date, startTime, duration) {
    const start = new Date(`${this.formatDate(date)} ${this.formatDateTime(date, startTime).split(' ')[1]}`);
    start.setMinutes(start.getMinutes() + duration);

    const year = start.getFullYear();
    const month = String(start.getMonth() + 1).padStart(2, '0');
    const day = String(start.getDate()).padStart(2, '0');
    const hours = String(start.getHours()).padStart(2, '0');
    const minutes = String(start.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:00`;
  }
}

export const bookingApi = new BookingService();